import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { INITIAL_JOB_POSTING_STEP } from '@/components/pages/job-posting/header/step-config';
import { PostingUnavailableDialog } from '@/components/pages/job-posting/posting-unavailable-dialog';
import { FreemiumMultiOpeningDialog } from '@/components/pages/job-posting/select-openings/freemium-multi-opening-dialog';
import {
  ChannelFormState,
  JobPostingForm,
  JobPostingStep,
  OpeningInfo,
  PostingResultData,
} from '@/components/pages/job-posting/types';
import { useJobPostingForm } from '@/components/pages/job-posting/use-job-posting-form';
import { buildChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { type ConfirmedDetailSave } from '@/components/pages/job-posting/utils/job-post-analytics';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useIsFirstJobPostOnAccount } from '@/hooks/use-is-first-job-post-on-account';
import { usePostHog } from '@/hooks/use-posthog';
import { queryKeys } from '@/query-keys';
import { getChannelName } from '@/utils/channel-names';
import {
  isSourceFreemium,
  shouldSkipSelectOpeningsStep,
} from '@/utils/is-source-freemium';
import {
  assignAllowedReturnTo,
  isAllowedReturnTo,
  resolveReturnTo,
  returnToFromCurrentLocation,
} from '@/utils/job-posting-return-to';
import {
  isPostJobFreshStart,
  parsePostJobEntryPoint,
  parsePostJobQuery,
  type PostJobEntryPoint,
} from '@/utils/parse-post-job-query';

import type { IneligibleOpening } from '@/components/pages/job-posting/types';
import {
  getPostingIneligibilityReason,
  type ListingStatus,
} from '@/utils/posting-eligibility';
import type { BaseSyntheticEvent } from 'react';
import { useChannelDescription } from '@/components/pages/job-posting/select-channels/use-channel-description';
import { removeOpeningFromChannel } from '@/components/pages/job-posting/utils/channel-utils';
import { useAuthContext } from '@/contexts/auth-context';
import { useFlag } from '@/flags';

const SELECTED_OPENING_IDS_KEY = 'job-posting-selected-opening-ids';
const INELIGIBLE_OPENINGS_KEY = 'job-posting-ineligible-openings';
const OPENINGS_INFO_KEY = 'job-posting-openings-info';

/**
 * Shape every caller of `openJobPostingForm` passes per opening.
 *
 * Only `id` is required. `title` is stored in the openings map so downstream
 * UI (the openings panel, etc.) doesn't need to wait on the channels API.
 * `active` and `isSourcingEnabled` default to `true`;
 */
interface OpeningSelection {
  id: string;
  title?: string;
  active?: boolean;
  isSourcingEnabled?: boolean;
  listingStatus?: ListingStatus;
}

type StartPostJobFlowProps = {
  entryPoint: PostJobEntryPoint;
  openings?: OpeningSelection[];
};

type OpenJobPostingFormProps = {
  openings: OpeningSelection[];
  channelSlugs?: string[];
  entryPoint: PostJobEntryPoint;
};
export interface JobPostingContextType {
  /**
   * Set of steps that have been completed.
   */
  completedSteps: Set<JobPostingStep>;
  markStepCompleted: (step: JobPostingStep) => void;
  /**
   * Distinct **real** channel slugs that are currently selected (derived from
   * `ChannelFormState.slug` for every entry in `channels`). Use this for membership
   * checks like `.includes(ZIPRECRUITER)` and for presenting the user with "which
   * channels did I pick?".
   *
   * Pool is included here as `'pool'` when the user has added the Pool channel.
   * For Vonq, multiple products of the same channel collapse into a single slug.
   */
  selectedChannelSlugs: string[];
  /**
   * Form-state keys currently in `channels` — `Object.keys(channels)`. Each Vonq
   * product is its own entry. Pool is keyed as `'pool'`. Use this when you need an
   * entry count (e.g. "is this the last channel form entry?").
   */
  selectedChannelKeys: string[];
  selectedOpeningIds: string[];
  setSelectedOpeningIds: Dispatch<SetStateAction<string[]>>;
  /**
   * Snapshot of `selectedOpeningIds` captured when the form was opened. Stays
   * constant across step changes and as openings are removed during the flow,
   * so consumers (e.g. the channels query) can fetch once on form open without
   * re-firing every time the user removes an opening or switches steps.
   */
  initialSelectedOpeningIds: string[];
  setInitialSelectedOpeningIds: Dispatch<SetStateAction<string[]>>;
  /**
   * Writes the current table selection into `initialSelectedOpeningIds` so
   * Channels can fetch. When the set changed after channels were already
   * loaded, drops stale `openingBudgets` and invalidates the channels queries.
   */
  confirmOpeningSelection: () => void;
  /**
   * Opens the free-plan dialog that blocks posting more than one opening.
   */
  openFreemiumMultiOpeningDialog: () => void;
  currentStep: JobPostingStep;
  setCurrentStep: Dispatch<SetStateAction<JobPostingStep>>;
  hookForm: UseFormReturn<JobPostingForm>;
  clearJobPostingStorage: () => void;
  totalBudget: number;
  handlePostJob: (e?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  poolOpeningsWithConflicts: string[] | undefined;
  setPoolOpeningsWithConflicts: Dispatch<SetStateAction<string[] | undefined>>;
  // List of openings that are inactive or sourcing-disabled
  ineligibleOpenings: IneligibleOpening[];
  /**
   * Map of `externalId → OpeningInfo` for every eligible opening currently in
   * the job-posting flow. Seeded by `openJobPostingForm` from each
   * `OpeningSelection` the caller provided. When the caller doesn't pass a
   * `title`, the entry is `{ externalId }` only and `useOpening` backfills the
   * title from the channels API.
   *
   * Prefer reading via the `useOpening` hook — it merges this stored metadata
   * with derived data (selected channels, opening-level budget, …) — instead
   * of consuming the map directly.
   */
  openings: Record<string, OpeningInfo>;
  setOpenings: Dispatch<SetStateAction<Record<string, OpeningInfo>>>;
  openJobPostingForm: (props: OpenJobPostingFormProps) => void;
  /**
   * Starts the manual posting flow's clock and fires `source_post_job:flow_start`.
   * `openJobPostingForm` and the Hire deep link call it; the Campaigns button
   * calls it before it navigates.
   */
  startPostJobFlow: (props: StartPostJobFlowProps) => void;
  /**
   * Records job details the server confirmed saving for an opening. A save
   * whose request went out in an earlier flow is dropped.
   */
  recordConfirmedDetailSave: (
    openingId: string,
    saved: ConfirmedDetailSave,
    flowGeneration?: number,
  ) => void;
  /** Changes each time the posting flow is cleared. */
  getFlowGeneration: () => number;
  /** Job details per opening as the server last confirmed saving them. */
  getConfirmedDetailSaves: () => Record<string, ConfirmedDetailSave>;
  postingResult: PostingResultData | null;
  setPostingResult: Dispatch<SetStateAction<PostingResultData | null>>;
  dismissResult: () => void;
  /**
   * Allowlisted path or absolute URL to restore when the recruiter quits.
   * Read from the current location; a refresh drops it and Quit uses the dashboard.
   */
  returnTo: string | null;
  /**
   * Leaves the posting flow and restores `returnTo`, or the dashboard when none is set.
   */
  exitJobPosting: () => void;
}

export const JobPostingContext = createContext<
  JobPostingContextType | undefined
>(undefined);

export function JobPostingProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const location = useLocation();
  const { allCampaignsPath, dashboardPath, postJobPath } = useNavigationPaths();
  const queryClient = useQueryClient();
  const { getTitle } = useChannelDescription();
  const { whoami, account } = useAuthContext();
  const isAsyncPostingEnabled = useFlag('hire-source-async-job-posting');
  const accountSlug = account?.slug ?? '';
  const isFreemium = isSourceFreemium(whoami);
  const hasSeededFromQueryRef = useRef(false);
  const posthog = usePostHog();
  const isFirstJobPostOnAccount = useIsFirstJobPostOnAccount();
  // Set when `source_post_job:flow_start` fires; read by useJobPostingForm to
  // compute `time_to_post_seconds` on posting_complete/posting_publish.
  const flowStartedAtRef = useRef<number | null>(null);
  // Job details per opening as the server last confirmed saving them, so the
  // funnel reports only persisted details.
  const confirmedDetailSavesRef = useRef<Record<string, ConfirmedDetailSave>>(
    {},
  );
  // A card save can finish after Quit; its result belongs to the flow that
  // sent it, not the next one.
  const flowGenerationRef = useRef(0);
  const getFlowGeneration = useCallback(() => flowGenerationRef.current, []);
  const recordConfirmedDetailSave = useCallback(
    (
      openingId: string,
      saved: ConfirmedDetailSave,
      flowGeneration?: number,
    ) => {
      if (
        flowGeneration !== undefined &&
        flowGeneration !== flowGenerationRef.current
      ) {
        return;
      }
      const prior = confirmedDetailSavesRef.current[openingId];
      confirmedDetailSavesRef.current = {
        ...confirmedDetailSavesRef.current,
        [openingId]: { ...saved, baseline: prior?.baseline ?? saved.baseline },
      };
    },
    [],
  );
  const getConfirmedDetailSaves = useCallback(
    () => confirmedDetailSavesRef.current,
    [],
  );
  const returnTo = resolveReturnTo({
    search: location.search,
    state: location.state,
    accountSlug,
  });

  const [selectedOpeningIds, setSelectedOpeningIds] = useLocalStorageState<
    string[]
  >(SELECTED_OPENING_IDS_KEY, []);
  const [ineligibleOpenings, setIneligibleOpenings] = useLocalStorageState<
    IneligibleOpening[]
  >(INELIGIBLE_OPENINGS_KEY, []);
  const [openings, setOpenings] = useLocalStorageState<
    Record<string, OpeningInfo>
  >(OPENINGS_INFO_KEY, {});
  const [initialSelectedOpeningIds, setInitialSelectedOpeningIds] =
    useState<string[]>(selectedOpeningIds);

  const [currentStep, setCurrentStep] = useState<JobPostingStep>(() =>
    shouldSkipSelectOpeningsStep(selectedOpeningIds.length, isFreemium)
      ? 'select-channels'
      : INITIAL_JOB_POSTING_STEP,
  );
  const [poolOpeningsWithConflicts, setPoolOpeningsWithConflicts] =
    useState<string[]>();
  const [isPostingUnavailableDialogOpen, setIsPostingUnavailableDialogOpen] =
    useState(false);
  const [
    isFreemiumMultiOpeningDialogOpen,
    setIsFreemiumMultiOpeningDialogOpen,
  ] = useState(false);
  const [postingResult, setPostingResult] = useState<PostingResultData | null>(
    null,
  );
  const hookFormRef = useRef<UseFormReturn<JobPostingForm> | null>(null);

  const [completedSteps, setCompletedSteps] = useState<Set<JobPostingStep>>(
    () =>
      shouldSkipSelectOpeningsStep(selectedOpeningIds.length, isFreemium)
        ? new Set<JobPostingStep>(['select-openings'])
        : new Set(),
  );
  const markStepCompleted = useCallback((step: JobPostingStep) => {
    setCompletedSteps(prev => {
      if (prev.has(step)) return prev;
      return new Set(prev).add(step);
    });
  }, []);

  /**
   * Clears the job posting storage and resets the state.
   * To be used when the user navigates away from the job posting form.
   */
  const clearJobPostingStorage = useCallback(() => {
    localStorage.removeItem(SELECTED_OPENING_IDS_KEY);
    localStorage.removeItem(INELIGIBLE_OPENINGS_KEY);
    localStorage.removeItem(OPENINGS_INFO_KEY);
    setSelectedOpeningIds([]);
    setInitialSelectedOpeningIds([]);
    setIneligibleOpenings([]);
    setOpenings({});
    setCurrentStep(INITIAL_JOB_POSTING_STEP);
    setCompletedSteps(new Set());
    setPoolOpeningsWithConflicts(undefined);
    setPostingResult(null);
    setIsFreemiumMultiOpeningDialogOpen(false);
    queryClient.removeQueries({ queryKey: queryKeys.jobPosting.poolConflicts });
    hookFormRef.current?.reset({});
    confirmedDetailSavesRef.current = {};
    flowGenerationRef.current += 1;
    // An abandoned flow's clock would otherwise time the next posting.
    flowStartedAtRef.current = null;
  }, [queryClient, setSelectedOpeningIds, setIneligibleOpenings, setOpenings]);

  const { hookForm, handlePostJob, isSubmitting } = useJobPostingForm({
    setPostingResult,
    setCurrentStep,
    flowStartedAtRef,
    isAsyncPostingEnabled,
    getFlowGeneration,
    onPostingBatchCreated: postingBatchId =>
      navigate(`${postJobPath}/${encodeURIComponent(postingBatchId)}`),
  });
  hookFormRef.current = hookForm;

  const dismissResult = useCallback(() => {
    clearJobPostingStorage();
    navigate(isFreemium ? allCampaignsPath : dashboardPath);
  }, [
    allCampaignsPath,
    clearJobPostingStorage,
    dashboardPath,
    isFreemium,
    navigate,
  ]);

  const exitJobPosting = useCallback(() => {
    const relativeReturnTo =
      returnTo &&
      returnTo.startsWith('/') &&
      !returnTo.startsWith('//') &&
      isAllowedReturnTo(returnTo, accountSlug)
        ? returnTo
        : null;
    clearJobPostingStorage();
    if (relativeReturnTo) {
      navigate(relativeReturnTo);
      return;
    }
    if (returnTo && assignAllowedReturnTo(returnTo)) {
      return;
    }
    navigate(dashboardPath);
  }, [accountSlug, clearJobPostingStorage, dashboardPath, navigate, returnTo]);

  /**
   * Seeds opening selection for the posting flow.
   * Returns eligible opening ids, or null when every opening is ineligible.
   */
  const seedOpenings = useCallback(
    (requested: OpeningSelection[]): string[] | null => {
      setCurrentStep(INITIAL_JOB_POSTING_STEP);
      setCompletedSteps(new Set());
      setPostingResult(null);
      setPoolOpeningsWithConflicts(undefined);
      hookFormRef.current?.reset({});

      const ineligible: IneligibleOpening[] = [];
      const nextOpenings: Record<string, OpeningInfo> = {};
      const eligibleIds: string[] = [];

      for (const opening of requested) {
        // ID-only inputs lack state for this check; paid submission validates it server-side.
        const hasEligibilityDetails =
          opening.active !== undefined ||
          opening.isSourcingEnabled !== undefined ||
          opening.listingStatus !== undefined;
        const reason = getPostingIneligibilityReason(
          opening,
          whoami?.account_listing_status_active === true &&
            hasEligibilityDetails,
        );

        if (reason) {
          ineligible.push({
            externalId: opening.id,
            title: opening.title ?? opening.id,
            reason,
          });
          continue;
        }

        eligibleIds.push(opening.id);
        nextOpenings[opening.id] = {
          externalId: opening.id,
          ...(opening.title ? { title: opening.title } : {}),
        };
      }

      setIneligibleOpenings(ineligible);
      setOpenings(nextOpenings);

      if (eligibleIds.length === 0 && ineligible.length > 0) {
        setIsPostingUnavailableDialogOpen(true);
        return null;
      }

      setSelectedOpeningIds(eligibleIds);
      setInitialSelectedOpeningIds(eligibleIds);

      if (shouldSkipSelectOpeningsStep(eligibleIds.length, isFreemium)) {
        setCurrentStep('select-channels');
        setCompletedSteps(new Set<JobPostingStep>(['select-openings']));
      } else {
        setCurrentStep(INITIAL_JOB_POSTING_STEP);
        setCompletedSteps(new Set());
      }

      return eligibleIds;
    },
    [
      isFreemium,
      setIneligibleOpenings,
      setOpenings,
      setSelectedOpeningIds,
      whoami?.account_listing_status_active,
    ],
  );

  const confirmOpeningSelection = useCallback(() => {
    const previousIds = initialSelectedOpeningIds;
    const nextIds = selectedOpeningIds;
    const nextIdSet = new Set(nextIds);
    const selectionChanged =
      previousIds.length !== nextIds.length ||
      previousIds.some(id => !nextIdSet.has(id));

    setInitialSelectedOpeningIds(nextIds);

    if (!selectionChanged) return;

    queryClient.invalidateQueries({
      queryKey: queryKeys.jobPosting.channelsRoot,
    });

    const removedIds = previousIds.filter(id => !nextIdSet.has(id));
    const channels = hookFormRef.current?.getValues('channels');
    if (!hookFormRef.current || !channels || removedIds.length === 0) return;

    const nextChannels = { ...channels };
    for (const openingId of removedIds) {
      for (const key of Object.keys(nextChannels)) {
        const updated = removeOpeningFromChannel(nextChannels[key], openingId);
        if (updated) {
          nextChannels[key] = updated;
        } else {
          delete nextChannels[key];
        }
      }
    }
    hookFormRef.current.setValue('channels', nextChannels);
  }, [initialSelectedOpeningIds, queryClient, selectedOpeningIds]);

  const openFreemiumMultiOpeningDialog = useCallback(() => {
    setIsFreemiumMultiOpeningDialogOpen(true);
  }, []);

  /**
   * Opens the job posting form. If the openings are ineligible, the alert dialog will be opened.
   *
   * When `channelSlugs` is provided, the channels are pre-populated on the form state
   *
   * A single pass is used to split into eligible/ineligible and seed the openings map so downstream UI has titles without an API roundtrip.
   */
  const startPostJobFlow = useCallback(
    ({ entryPoint, openings: requested = [] }: StartPostJobFlowProps) => {
      flowStartedAtRef.current = Date.now();

      const singleOpening = requested.length === 1 ? requested[0] : undefined;
      posthog.capture('source_post_job:flow_start', {
        entry_point: entryPoint,
        // Cue postings start in Cue, which never enters this flow.
        post_mode: 'Manual',
        is_first_job_post_on_account: isFirstJobPostOnAccount,
        ...(entryPoint === 'opening' && singleOpening
          ? {
              opening_name: singleOpening.title,
              opening_external_id: singleOpening.id,
            }
          : {}),
      });
    },
    [isFirstJobPostOnAccount, posthog],
  );

  const openJobPostingForm = useCallback(
    ({
      openings: requested,
      channelSlugs,
      entryPoint,
    }: OpenJobPostingFormProps) => {
      const eligibleIds = seedOpenings(requested);
      if (eligibleIds === null) {
        return;
      }

      // Pre-populate the form when the caller pre-selected channels for the user.
      if (channelSlugs && channelSlugs.length > 0 && hookFormRef.current) {
        const channels = channelSlugs.reduce<Record<string, ChannelFormState>>(
          (acc, slug) => ({
            ...acc,
            [slug]: buildChannelDefaults({
              channelSlug: slug,
              channelName: getChannelName(slug),
              displayTitle: getTitle(slug, getChannelName(slug)),
              openingIds: eligibleIds,
              unsupportedOpenings: [],
            }),
          }),
          {},
        );

        hookForm.setValue('channels', channels);
      }

      startPostJobFlow({ entryPoint, openings: requested });

      const origin = returnToFromCurrentLocation(
        location.pathname,
        location.search,
        accountSlug,
      );
      navigate(postJobPath, {
        state: origin ? { returnTo: origin } : undefined,
      });
    },
    [
      accountSlug,
      getTitle,
      hookForm,
      location.pathname,
      location.search,
      navigate,
      postJobPath,
      seedOpenings,
      startPostJobFlow,
    ],
  );

  // Deep link from Hire: /post-job?openingIds=… or /post-job?fresh=1
  useEffect(() => {
    if (!location.pathname.includes('/post-job')) {
      hasSeededFromQueryRef.current = false;
      return;
    }

    if (hasSeededFromQueryRef.current) {
      return;
    }

    const nextReturnTo = resolveReturnTo({
      search: location.search,
      state: location.state,
      accountSlug,
    });

    const stripPostJobQuery = () => {
      if (!location.search) {
        return;
      }
      navigate(
        { pathname: location.pathname, search: '' },
        {
          replace: true,
          state: nextReturnTo ? { returnTo: nextReturnTo } : undefined,
        },
      );
    };

    const entryPoint = parsePostJobEntryPoint(location.search);

    if (isPostJobFreshStart(location.search)) {
      hasSeededFromQueryRef.current = true;
      // Campaigns starts the flow, then lands here with fresh=1 and no entry
      // point, so its clock survives the reset.
      const flowStartedAt = flowStartedAtRef.current;
      clearJobPostingStorage();
      if (entryPoint) startPostJobFlow({ entryPoint });
      else flowStartedAtRef.current = flowStartedAt;
      stripPostJobQuery();
      return;
    }

    const queryOpenings = parsePostJobQuery(location.search);
    if (queryOpenings) {
      hasSeededFromQueryRef.current = true;
      seedOpenings(queryOpenings);
      if (entryPoint) startPostJobFlow({ entryPoint, openings: queryOpenings });
      stripPostJobQuery();
      return;
    }

    if (nextReturnTo && location.search) {
      stripPostJobQuery();
    }
  }, [
    accountSlug,
    clearJobPostingStorage,
    location.pathname,
    location.search,
    location.state,
    navigate,
    seedOpenings,
    startPostJobFlow,
  ]);

  // Freemium accounts with more than one selected opening stay on select-openings.
  useEffect(() => {
    if (!isFreemium) return;
    if (selectedOpeningIds.length <= 1) return;
    if (currentStep !== 'select-channels') return;

    setCurrentStep(INITIAL_JOB_POSTING_STEP);
    setCompletedSteps(prev => {
      if (!prev.has('select-openings')) return prev;
      const next = new Set(prev);
      next.delete('select-openings');
      return next;
    });
  }, [currentStep, isFreemium, selectedOpeningIds.length]);

  const channels = useWatch({ control: hookForm.control, name: 'channels' });
  // Form-state keys: one entry per channel form slice. Pool is keyed as 'pool'.
  const selectedChannelKeys = useMemo(
    () => Object.keys(channels ?? {}),
    [channels],
  );
  // Distinct real channel slugs. Collapses multiple Vonq products into a single slug
  // (e.g. two Monster products → ['monster']). Pool appears as 'pool' when selected.
  const selectedChannelSlugs = useMemo(() => {
    const slugs: string[] = [];
    for (const channel of Object.values(channels ?? {})) {
      if (!slugs.includes(channel.slug)) slugs.push(channel.slug);
    }
    return slugs;
  }, [channels]);

  const totalBudget = !channels
    ? 0
    : Object.values(channels).reduce(
        (acc, channel) => acc + (channel.totalChannelBudget ?? 0),
        0,
      );

  /* Handles the change of the posting unavailable dialog. If the dialog is closed, the state will be reset. */
  const handlePostingUnavailableDialogChange = (open: boolean) => {
    if (!open) {
      setIsPostingUnavailableDialogOpen(false);
      setIneligibleOpenings([]);
    }
  };

  const contextValue = useMemo<JobPostingContextType>(
    () => ({
      completedSteps,
      markStepCompleted,
      selectedChannelSlugs,
      selectedChannelKeys,
      selectedOpeningIds,
      setSelectedOpeningIds,
      initialSelectedOpeningIds,
      setInitialSelectedOpeningIds,
      confirmOpeningSelection,
      openFreemiumMultiOpeningDialog,
      currentStep,
      setCurrentStep,
      hookForm,
      totalBudget,
      clearJobPostingStorage,
      handlePostJob,
      isSubmitting,
      poolOpeningsWithConflicts,
      setPoolOpeningsWithConflicts,
      ineligibleOpenings,
      openings,
      setOpenings,
      openJobPostingForm,
      startPostJobFlow,
      recordConfirmedDetailSave,
      getFlowGeneration,
      getConfirmedDetailSaves,
      postingResult,
      setPostingResult,
      dismissResult,
      returnTo,
      exitJobPosting,
    }),
    [
      completedSteps,
      markStepCompleted,
      selectedChannelSlugs,
      selectedChannelKeys,
      selectedOpeningIds,
      setSelectedOpeningIds,
      initialSelectedOpeningIds,
      confirmOpeningSelection,
      openFreemiumMultiOpeningDialog,
      currentStep,
      setCurrentStep,
      hookForm,
      totalBudget,
      clearJobPostingStorage,
      handlePostJob,
      isSubmitting,
      poolOpeningsWithConflicts,
      setPoolOpeningsWithConflicts,
      ineligibleOpenings,
      openings,
      setOpenings,
      openJobPostingForm,
      startPostJobFlow,
      recordConfirmedDetailSave,
      getFlowGeneration,
      getConfirmedDetailSaves,
      postingResult,
      setPostingResult,
      dismissResult,
      returnTo,
      exitJobPosting,
    ],
  );

  return (
    <JobPostingContext.Provider value={contextValue}>
      {children}
      <PostingUnavailableDialog
        open={isPostingUnavailableDialogOpen}
        onOpenChange={handlePostingUnavailableDialogChange}
        ineligibleOpenings={ineligibleOpenings}
      />
      <FreemiumMultiOpeningDialog
        open={isFreemiumMultiOpeningDialogOpen}
        onOpenChange={setIsFreemiumMultiOpeningDialogOpen}
        selectedOpeningIds={selectedOpeningIds}
        openings={openings}
      />
    </JobPostingContext.Provider>
  );
}

export function useJobPostingContext(): JobPostingContextType {
  const context = useContext(JobPostingContext);

  if (!context) {
    throw new Error(
      'useJobPostingContext must be used within a JobPostingProvider',
    );
  }

  return context;
}
